// src/components/quality/CreateCriteriaModal.js
import { useState } from 'react';

const CreateCriteriaModal = ({ projectId, onCreate, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    weight: 0.3,
    comment_prompt: '',
    positive: true,
    critical: false,
    type_skills: 'soft',
    dependency_field: '',
    dependency_values: '',
    default_option_id: null,
    options: [
      { text: 'Да', score: 1.0, order: 1 },
      { text: 'Нет', score: 0.0, order: 2 },
    ],
  });

  const handleOptionChange = (index, field, value) => {
    const newOptions = [...formData.options];
    newOptions[index][field] = field === 'score' ? parseFloat(value) || 0 : value;
    setFormData({ ...formData, options: newOptions });
  };

  const addOption = () => {
    const maxOrder = Math.max(...formData.options.map((o) => o.order), 0);
    setFormData({
      ...formData,
      options: [...formData.options, { text: '', score: 0, order: maxOrder + 1 }],
    });
  };

  const removeOption = (index) => {
    const newOptions = formData.options.filter((_, i) => i !== index);
    setFormData({ ...formData, options: newOptions });
  };

  // Перемещение варианта вверх
  const moveOptionUp = (index) => {
    if (index === 0) return;
    const newOptions = [...formData.options];
    const temp = newOptions[index - 1].order;
    newOptions[index - 1].order = newOptions[index].order;
    newOptions[index].order = temp;

    setFormData({
      ...formData,
      options: newOptions.sort((a, b) => a.order - b.order),
    });
  };

  // Перемещение варианта вниз
  const moveOptionDown = (index) => {
    if (index === formData.options.length - 1) return;
    const newOptions = [...formData.options];
    const temp = newOptions[index + 1].order;
    newOptions[index + 1].order = newOptions[index].order;
    newOptions[index].order = temp;

    setFormData({
      ...formData,
      options: newOptions.sort((a, b) => a.order - b.order),
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      window.showToast?.('Введите название критерия');
      return;
    }
    if (isNaN(formData.weight) || formData.weight <= 0) {
      window.showToast?.('Некорректный вес');
      return;
    }
    if (formData.options.some((opt) => !opt.text.trim())) {
      window.showToast?.('Все варианты должны иметь текст');
      return;
    }

    const sortedOptions = [...formData.options].sort((a, b) => a.order - b.order);
    
    const payload = {
      ...formData,
      options: sortedOptions,
      project_id: projectId,
      default_option_id: formData.default_option_id,
      type_skills: formData.type_skills,
    };
    
    // Добавляем зависимости, если выбрано поле
    if (formData.dependency_field) {
      const values = formData.dependency_values
        .split(',')
        .map(v => v.trim())
        .filter(v => v);
      
      if (values.length > 0) {
        payload.dependencies = values.map(value => ({
          field_name: formData.dependency_field,
          field_value: value
        }));
      }
    }
    
    onCreate(payload);
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <h3>Создать новый критерий</h3>
          {/* Кнопка закрытия */}
          <button className="text-modal-close" onClick={onClose}>
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Название критерия *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Например: Вежливость"
              required
              autoFocus
            />
          </div>

          <div className="form-group">
            <label>Вес *</label>
            <input
              type="number"
              step="0.1"
              value={formData.weight}
              onChange={(e) => setFormData({ ...formData, weight: parseFloat(e.target.value) || 0.3 })}
              required
            />
          </div>

          <div className="form-group">
            <label>Подсказка для комментария (необязательно)</label>
            <textarea
              value={formData.comment_prompt}
              onChange={(e) => setFormData({ ...formData, comment_prompt: e.target.value })}
              placeholder="Например: Укажите причину. Можно использовать многострочный текст."
              rows="3"
              className="comment-textarea"
            />
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={formData.positive}
                onChange={(e) => setFormData({ ...formData, positive: e.target.checked })}
              />
              Позитивный критерий (например: «Вежливость»)
            </label>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={formData.critical}
                onChange={(e) => setFormData({ ...formData, critical: e.target.checked })}
              />
              Критический критерий (например: «Утечка данных»)
            </label>
          </div>

          <div className="form-group">
            <label>Тип навыков</label>
            <select
              value={formData.type_skills}
              onChange={(e) => setFormData({ ...formData, type_skills: e.target.value })}
              className="project-select"
            >
              <option value="soft">Soft</option>
              <option value="hard">Hard</option>
            </select>
          </div>

          <div className="form-group">
            <label>Поле, от которого зависит критерий (необязательно)</label>
            <select
              value={formData.dependency_field}
              onChange={(e) => setFormData({ ...formData, dependency_field: e.target.value })}
              className="project-select"
            >
              <option value="">-- Не зависит от поля --</option>
              <option value="impact">Impact</option>
              <option value="assessment_type">Тип оценки</option>
              <option value="srq">SRQ</option>
              <option value="status">Статус</option>
            </select>
          </div>



          {formData.dependency_field && (
            <div className="form-group">
              <label>Значения поля (через запятую) *</label>
              <input
                type="text"
                value={formData.dependency_values}
                onChange={(e) => setFormData({ ...formData, dependency_values: e.target.value })}
                placeholder="Например: High,Medium"
                className="project-select"
                required
              />
            </div>
          )}

          <div className="options-header">
            <h4>Варианты ответов</h4>
            {formData.dependency_field && (
              <label className="default-radio" style={{ marginLeft: '20px' }}>
                <input
                  type="radio"
                  name="default_option"
                  checked={formData.default_option_id === null}
                  onChange={() => setFormData({ ...formData, default_option_id: null })}
                />
                Без дефолтного
              </label>
            )}
          </div>
          <table className="options-table">
            <thead>
              <tr>
                <th>Текст</th>
                <th>Балл</th>
                <th>Порядок</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {formData.options
                .sort((a, b) => a.order - b.order)
                .map((opt, index) => (
                  <tr key={index}>
                    <td>
                      <input
                        type="text"
                        value={opt.text}
                        onChange={(e) => handleOptionChange(index, 'text', e.target.value)}
                        required
                        className="option-input-text"
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        step="0.1"
                        value={opt.score}
                        onChange={(e) => handleOptionChange(index, 'score', e.target.value)}
                        required
                        className="option-input-score"
                      />
                    </td>
                    <td>
                      <span className="option-order">{opt.order}</span>
                    </td>
                    <td>
                      <div className="option-actions">
                        <button
                          type="button"
                          onClick={() => moveOptionUp(index)}
                          disabled={index === 0}
                          className="btn-move-up"
                          title="Переместить вверх"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          onClick={() => moveOptionDown(index)}
                          disabled={index === formData.options.length - 1}
                          className="btn-move-down"
                          title="Переместить вниз"
                        >
                          ↓
                        </button>
                        {formData.options.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeOption(index)}
                            className="text-modal-close"
                            title="Удалить вариант"
                          >
                            ×
                          </button>
                        )}
                        {formData.dependency_field && (
                          <label className="default-radio">
                            <input
                              type="radio"
                              name="default_option"
                              checked={formData.default_option_id === opt.order}
                              onChange={() => setFormData({ ...formData, default_option_id: opt.order })}
                            />
                            Дефолт
                          </label>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>

          <button type="button" onClick={addOption} className="btn-warning">
            Добавить вариант
          </button>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn-cancel">
              Отмена
            </button>
            <button type="submit" className="btn-create">
              Создать критерий
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCriteriaModal;
